import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useBlogList } from '../hooks/useBlog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => {
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const BlogCard = ({ post }) => (
  <Card
    className="overflow-hidden border-2 hover:border-blue-300 transition-all hover:shadow-lg"
    data-testid="blog-card"
  >
    {post.image && (
      <img src={post.image} alt={post.title} className="w-full h-64 object-cover" />
    )}
    <CardContent className="p-8 space-y-4">
      <div className="flex items-center space-x-4 text-sm text-gray-500">
        <div className="flex items-center space-x-1">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(post.publish_at)}</span>
        </div>
        <span className="text-blue-600 font-medium">{post.category}</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors">
        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
      </h2>
      <p className="text-gray-600 leading-relaxed">{post.excerpt}</p>
      <div className="pt-4">
        <Link to={`/blog/${post.slug}`}>
          <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
            Read More
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </CardContent>
  </Card>
);

const BlogHero = () => (
  <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto text-center">
      <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Senior Care Insights</h1>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Expert advice and guidance on navigating healthcare for seniors
      </p>
    </div>
  </section>
);

const BlogCta = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50">
    <div className="max-w-4xl mx-auto text-center space-y-6">
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
        Stay Informed About Senior Care
      </h2>
      <p className="text-xl text-gray-600">
        Have questions about senior care or need personalized guidance?
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <Link to="/contact">
          <Button size="lg" className="bg-coral-500 hover:bg-coral-600 text-white">
            Book Your Consultation Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
        <a
          href={`${BACKEND_URL}/api/blog/rss`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-blue-600 hover:underline inline-flex items-center"
          data-testid="blog-rss-link"
        >
          Subscribe via RSS
        </a>
      </div>
    </div>
  </section>
);

const Blog = () => {
  const { posts, isLoading } = useBlogList();

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Senior Care Insights | Bring-A-Nurse Senior Companion</title>
        <meta
          name="description"
          content="Expert advice and guidance on navigating healthcare for seniors. Articles on doctor visits, medication management, advocacy, and family caregiving from Bring-A-Nurse Senior Companion."
        />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Bring-A-Nurse Senior Care Insights"
          href={`${BACKEND_URL}/api/blog/rss`}
        />
      </Helmet>

      <BlogHero />

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {isLoading && (
            <p className="text-center text-gray-500" data-testid="blog-loading">
              Loading posts…
            </p>
          )}

          {!isLoading && posts.length === 0 && (
            <div className="text-center text-gray-500 py-10" data-testid="blog-empty">
              <p className="text-lg">New articles are on the way.</p>
              <p className="text-sm mt-2">
                Check back soon for the latest senior care insights.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="blog-grid">
            {posts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </div>
      </section>

      <BlogCta />
    </div>
  );
};

export default Blog;
